/**
 * View state only (CLAUDE.md stack).
 *
 * The engine owns physical state; this store holds the latest `Snapshot` and whatever the UI
 * needs to know about itself — which view is open, what is selected, what confirmation is
 * pending. Nothing here is simulation state, and nothing here is ever fed back into the engine
 * except as an `Action`.
 */

import { create } from 'zustand';
import type { ScoreCard } from '../engine/scoring';
import type { SaveFile, Snapshot } from '../engine/snapshot';
import type { Difficulty } from '../engine/types';
import type { SiteSlot, Speed } from '../worker/protocol';
import type { WhatIfTrace } from '../worker/whatif';

export type ViewId =
  | 'control-room'
  | 'reservoir'
  | 'powerhouse'
  | 'spillway'
  | 'river'
  | 'market'
  | 'alarms'
  | 'debrief';

export interface PendingConfirm {
  label: string;
  detail: string;
  confirm(): void;
}

export interface Settings {
  /** Imperial display conversion (the engine never sees imperial units). */
  imperial: boolean;
  reducedMotion: boolean;
  showGlossary: boolean;
  defaultSpeed: Speed;
}

export interface SessionState {
  phase: 'launcher' | 'running' | 'debrief';
  siteId: string;
  scenarioId: string;
  difficulty: Difficulty;
  seed: number;

  /**
   * The **active** plant's snapshot and site id.
   *
   * Cascade scenarios run two plants, and the operator switches between their consoles. Keeping
   * `snapshot` and `siteId` as the active pair means every view reads exactly what it read when
   * there was only one plant — no view knows the cascade exists.
   */
  snapshot: Snapshot | null;
  primarySnapshot: Snapshot | null;
  secondarySnapshot: Snapshot | null;
  activeSite: SiteSlot;
  secondaryCard: ScoreCard | null;
  card: ScoreCard | null;
  view: ViewId;
  speed: Speed;
  paused: boolean;
  fault: string | null;

  selectedUnit: number;
  selectedGate: number;
  pendingConfirm: PendingConfirm | null;
  glossaryTerm: string | null;
  whatIf: WhatIfTrace[] | null;
  whatIfPending: boolean;
  saves: SaveFile[];
  settings: Settings;
  toast: string | null;

  setPhase(p: SessionState['phase']): void;
  setSnapshots(primary: Snapshot, secondary: Snapshot | null): void;
  setActiveSite(slot: SiteSlot): void;
  setCard(c: ScoreCard): void;
  setSecondaryCard(c: ScoreCard | null): void;
  setView(v: ViewId): void;
  setSpeed(s: Speed): void;
  setPaused(p: boolean): void;
  setFault(f: string | null): void;
  selectUnit(i: number): void;
  selectGate(i: number): void;
  requestConfirm(c: PendingConfirm | null): void;
  setGlossary(term: string | null): void;
  setWhatIf(t: WhatIfTrace[] | null): void;
  setWhatIfPending(p: boolean): void;
  setSaves(s: SaveFile[]): void;
  updateSettings(p: Partial<Settings>): void;
  setToast(t: string | null): void;
  configure(p: Partial<Pick<SessionState, 'siteId' | 'scenarioId' | 'difficulty' | 'seed'>>): void;
}

export const useSession = create<SessionState>((set) => ({
  phase: 'launcher',
  siteId: 'kettle-narrows',
  scenarioId: 's0-handover',
  difficulty: 'operator',
  seed: 20260830,

  snapshot: null,
  primarySnapshot: null,
  secondarySnapshot: null,
  activeSite: 'primary',
  secondaryCard: null,
  card: null,
  view: 'control-room',
  speed: 1,
  paused: true,
  fault: null,

  selectedUnit: 0,
  selectedGate: 0,
  pendingConfirm: null,
  glossaryTerm: null,
  whatIf: null,
  whatIfPending: false,
  saves: [],
  settings: {
    imperial: false,
    reducedMotion:
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    showGlossary: false,
    defaultSpeed: 60,
  },
  toast: null,

  setPhase: (phase) => set({ phase }),
  setSnapshots: (primary, secondary) =>
    set((s) => {
      const active = s.activeSite === 'secondary' && secondary ? secondary : primary;
      return {
        primarySnapshot: primary,
        secondarySnapshot: secondary,
        snapshot: active,
        siteId: active.siteId,
      };
    }),
  setActiveSite: (activeSite) =>
    set((s) => {
      const active = activeSite === 'secondary' ? s.secondarySnapshot : s.primarySnapshot;
      if (!active) return { activeSite };
      // Selection is per-plant; a gate index from a 4-gate site means nothing at a 14-gate one.
      return { activeSite, snapshot: active, siteId: active.siteId, selectedGate: 0, selectedUnit: 0 };
    }),
  setCard: (card) => set({ card }),
  setSecondaryCard: (secondaryCard) => set({ secondaryCard }),
  setView: (view) => set({ view }),
  setSpeed: (speed) => set({ speed }),
  setPaused: (paused) => set({ paused }),
  setFault: (fault) => set({ fault }),
  selectUnit: (selectedUnit) => set({ selectedUnit }),
  selectGate: (selectedGate) => set({ selectedGate }),
  requestConfirm: (pendingConfirm) => set({ pendingConfirm }),
  setGlossary: (glossaryTerm) => set({ glossaryTerm }),
  setWhatIf: (whatIf) => set({ whatIf }),
  setWhatIfPending: (whatIfPending) => set({ whatIfPending }),
  setSaves: (saves) => set({ saves }),
  updateSettings: (p) => set((s) => ({ settings: { ...s.settings, ...p } })),
  setToast: (toast) => set({ toast }),
  configure: (p) => set(p),
}));
